import { CanvasEx } from '@/typings/canvas';
import {
    BaseCanvasElement,
    CanvasElement,
    CanvasElementType,
    ImagePlaceholderElement,
    VectorPlaceholderElement
} from '@/typings/customilyElements/canvasElements';
import { ElementsGroup } from '@/typings/customilyElements/elementsGroup';
import { ElementsPageEps } from '@/typings/customilyElements/elementsPageEps';
import type { ListElements } from '@/typings/customilyElements/layer';
import { ListElementType } from '@/typings/customilyElements/listElementType';
import { ActiveSelection } from 'fabric/fabric-impl';
import { debounce } from 'lodash';

const isRootCanvasElementValid = (canvasElement: CanvasElement) => {
    const hasPageEpsId = canvasElement.pageEpsId;
    const hasGroupId = canvasElement.groupId;

    return !hasPageEpsId && !hasGroupId;
};

export const focusCanvas = (canvas: CanvasEx) => {
    if (canvas.eventContainer) {
        $(`#${canvas.eventContainer}`).focus();
    }
};

export const getComputedElementType = (object: CanvasElement, objectType?: CanvasElementType): string => {
    const type = objectType || object.type;
    const types: Record<string, () => string> = {
        'image-placeholder': () => {
            const element = object as ImagePlaceholderElement;
            if (element.isProductGuidelines) {
                return 'image-product-guidelines';
            }
            if (element.mapConfig) {
                return 'image-placeholder-map';
            }
            if (element.dynamicImagesPath) {
                return element.isMoonPhase ? 'image-moonphase' : 'image-placeholder-dynamic';
            }
            if (element.crossword) {
                return 'image-placeholder-crossword';
            }
            if (element.calendar) {
                return 'image-placeholder-calendar';
            }
            return 'image-placeholder';
        },
        'vector-placeholder': () => {
            const element = object as VectorPlaceholderElement;
            if (element.starMapConfig) {
                return 'vector-placeholder-starmap';
            }
            if (element.qrConfig) {
                return 'vector-qrcode';
            }
            if (element.allowUserUpload) {
                return 'vector-placeholder';
            }
            if (element.isSolidBackground) {
                return 'vector-solid-background';
            }
            return 'vector-placeholder-dynamic';
        }
    };

    return types[type]?.() || type;
};

export const getCanvasElementIcon = (object?: CanvasElement, objectType?: string) => {
    if (!object && !objectType?.length) {
        throw new Error('Object type not provided');
    }

    const type = objectType || getComputedElementType(object!);

    const icons: Record<string, string> = {
        'text-transform': require('@/components/Layers/assets/warped-text.svg'),
        'bounded-text': require('@/components/Layers/assets/text.svg'),
        'path-itext': require('@/components/Layers/assets/curvedtext.svg'),
        'image-placeholder': require('@/components/Layers/assets/imageplaceholder.svg'),
        'image-placeholder-dynamic': require('@/components/Layers/assets/dynamicimage.svg'),
        'image-moonphase': require('@/components/Layers/assets/moonphase.svg'),
        'image-placeholder-map': require('@/components/Layers/assets/map.svg'),
        'image-placeholder-crossword': require('@/components/Layers/assets/crossword.svg'),
        'image-placeholder-calendar': require('@/components/Layers/assets/calendar.svg'),
        'image-product-guidelines': require('@/components/Layers/assets/product-guidelines.svg'),
        'vector-placeholder': require('@/components/Layers/assets/vectorplaceholder.svg'),
        'vector-placeholder-dynamic': require('@/components/Layers/assets/dynamicvector.svg'),
        'vector-placeholder-starmap': require('@/components/Layers/assets/vectorplaceholder.svg'),
        'vector-qrcode': require('@/components/Layers/assets/qrcode.svg'),
        'vector-solid-background': require('@/components/Layers/assets/vectorplaceholder.svg'),
        page: require('@/components/Layers/assets/page.svg'),
        group: require('@/components/Layers/assets/groupOpen.svg')
    };

    return icons[type];
};

export const getCanvasElementTitle = (object: Partial<CanvasElement>, objectType?: string) => {
    if (!object && !objectType?.length) {
        throw new Error('Object type not provided');
    }
    const id = object.id;
    const type = objectType || getComputedElementType(object as CanvasElement);

    const titles: Record<string, string> = {
        'text-transform': `Warped Text ${id}`,
        'bounded-text': `Text ${id}`,
        'path-itext': `Curved Text ${id}`,
        'image-placeholder': `Image Upload ${id}`,
        'image-placeholder-dynamic': `Image Selection ${id}`,
        'image-moonphase': `Moon phase ${id}`,
        'image-placeholder-crossword': `Crossword ${id}`,
        'image-placeholder-calendar': `Calendar ${id}`,
        'image-placeholder-map': `Map ${id}`,
        'vector-placeholder': `Vector Selection ${id}`,
        'vector-placeholder-upload': `Vector Upload ${id}`,
        'vector-placeholder-map': `Star Map ${id}`,
        'image-product-guidelines': `Product Guidelines ${id}`,
        'vector-solid-background': `Solid Background`,
        group: 'Layout'
    };

    return titles[type];
};

/**
 * Wherever this function is used, should be replaced by getCanvasElementTitle function
 */
export const getTitleForCanvasElement = (object: CanvasElement): string => {
    if (object && object.type && (object.name == undefined || object.name === '')) {
        if (object.type === 'bounded-text') {
            return 'Text ' + object.id;
        }
        if (object.type === 'path-itext') {
            return 'Curved Text ' + object.id;
        }
        if (object.type === 'text-transform') {
            return 'Warped Text ' + object.id;
        }
        if (object.type === 'image' || object.type === 'image-placeholder') {
            if (object.mapConfig) {
                return `Map ${object.id}`;
            } else if (object.isProductGuidelines) {
                return 'Product Guidelines ' + object.id;
            } else if (!object.dynamicImagesPath && !object.crossword && !object.calendar) {
                return 'Image Upload ' + object.id;
            } else if (object.crossword) {
                return 'Crossword ' + object.id;
            } else if (object.calendar) {
                return 'Calendar ' + object.id;
            } else if (object.isMoonPhase) {
                return 'Moon phase ' + object.id;
            } else {
                return 'Image Selection ' + object.id;
            }
        }
        if (object.type === 'vector-placeholder') {
            if (object.starMapConfig) {
                return `Star Map ${object.id}`;
            } else if (object.qrConfig) {
                return `QR Code ${object.id}`;
            } else if (object.isSolidBackground) {
                return 'Solid Background';
            } else if (!object.allowUserUpload) {
                return 'Vector Selection ' + object.id;
            } else {
                return 'Vector Upload ' + object.id;
            }
        }
    } else if (object && object.name) {
        return object.name;
    }

    return '';
};

export const selectCanvasElements = (elements: BaseCanvasElement[], canvas: CanvasEx, forceSelection = false) => {
    try {
        const elementUuids = new Set(
            elements.map((element) => (element.uuid ? `uuid:${element.uuid}` : `id:${element.id}`))
        );
        const targetElements = canvas.getObjects().filter((object) => {
            if (object.uuid) {
                return elementUuids.has(`uuid:${object.uuid}`);
            }
            return elementUuids.has(`id:${object.id}`);
        });

        if (!targetElements || !targetElements.length) {
            canvas.discardActiveObject();
        } else {
            if (targetElements.length === 1) {
                if ((!targetElements[0].locked && !targetElements[0].lockedByGroup) || forceSelection) {
                    canvas.setActiveObject(targetElements[0]);
                }
            } else {
                // If there's an active element already create a selection with all the elements
                //  except for locked elements
                canvas.discardActiveObject();
                const objects = forceSelection ? targetElements : targetElements.filter((e) => !e.locked);

                const selection = new window.fabric.ActiveSelection(objects, { canvas });
                selection.set('lockUniScaling', true);
                canvas.setActiveObject(selection);
            }
        }

        focusCanvas(canvas);
        canvas.requestRenderAll();
    } catch (error) {
        console.error('An error occurred trying to select the canvas elements', error);
    }
};

export const updateCanvasSelection = (canvas: CanvasEx) => {
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
        if (activeObject.type === 'activeSelection') {
            const elements = (activeObject as ActiveSelection).getObjects() as BaseCanvasElement[];

            // Filter locked elements from current selection
            canvas.discardActiveObject();
            const selection = new window.fabric.ActiveSelection(
                elements.filter((e) => !e.locked),
                { canvas }
            );
            selection.set('lockUniScaling', true);
            canvas.setActiveObject(selection);
        } else {
            if ((activeObject as BaseCanvasElement).locked) {
                canvas.discardActiveObject();
            }
        }

        canvas.requestRenderAll();
    }
};

/**
 * Sort the elements in the given canvas by their zIndex
 * @param canvas canvas to sort
 */
export const updateElementsOrderFromZIndex = (canvas: CanvasEx) => {
    const pageEpsElements = canvas?.elementsPageEps;
    const canvasElements = canvas._objects as CanvasElement[];
    const groupElements = canvas?.elementsGroups;

    const treeItems = getTreeItems({
        pageEpsElements,
        canvasElements,
        groupElements
    });

    updateElementOrder(treeItems, canvas, false);
};

export const setElementLock = (element: BaseCanvasElement, canvas: CanvasEx, locked: boolean) => {
    if (element) {
        element.set('locked', locked);
        canvas.fire('object:modified', {
            target: element
        });

        canvas.requestRenderAll();
    }
};

export const toggleElementLock = (element: BaseCanvasElement, canvas: CanvasEx) => {
    if (element) {
        // Deselect the object if it's currently selected
        if (canvas.getActiveObject() === element) {
            canvas.discardActiveObject();
        }

        setElementLock(element, canvas, !element.locked);
    }
};

export const setElementVisibility = (element: BaseCanvasElement, canvas: CanvasEx, visible: boolean) => {
    if (element) {
        element.set('visible', visible);
        canvas.fire('object:modified', {
            target: element
        });

        canvas.requestRenderAll();
    }
};

export const toggleElementVisibility = (element: BaseCanvasElement, canvas: CanvasEx) => {
    if (element) {
        // Deselect the object if it's currently selected
        if (canvas.getActiveObject() === element) {
            canvas.discardActiveObject();
        }

        setElementVisibility(element, canvas, !element.visible);
    }
};

export const toggleElementPrintArea = (element: BaseCanvasElement, canvas: CanvasEx) => {
    if (element) {
        // Deselect the object if it's currently selected
        if (canvas.getActiveObject() === element) {
            canvas.discardActiveObject();
        }

        element.set('isPrintArea', !element.isPrintArea);
        element.setEditedProp('isPrintArea');
        canvas.fire('object:modified', {
            target: element
        });

        canvas.requestRenderAll();
    }
};

export const toggleElementOverlay = (element: BaseCanvasElement, canvas: CanvasEx) => {
    if (element) {
        // Deselect the object if it's currently selected
        if (canvas.getActiveObject() === element) {
            canvas.discardActiveObject();
        }

        element.set('isOverlay', !element.isOverlay);
        element.setEditedProp('isOverlay');

        // If overlay was activated, move this object above everything else and disable overlay for any other elements
        if (element.isOverlay) {
            canvas
                .getObjects()
                .filter((el) => el !== element)
                .forEach((el) => {
                    if (el.isOverlay) {
                        el.set('isOverlay', false);
                        el.setEditedProp('isOverlay');
                        canvas.fire('object:modified', {
                            target: el
                        });
                    }
                });

            element.zIndex = 2e32;
            updateElementsOrderFromZIndex(canvas);
        }

        canvas.fire('object:modified', {
            target: element
        });

        canvas.requestRenderAll();
    }
};

export const toggleElementColorOption = (element: BaseCanvasElement, canvas: CanvasEx): boolean => {
    if (element) {
        // Deselect the object if it's currently selected
        if (canvas.getActiveObject() === element) {
            canvas.discardActiveObject();
        }

        // Only dynamic images can be color options
        const isDynamicImage =
            element.type === 'image-placeholder' && (element as ImagePlaceholderElement).dynamicImagesPath;
        if (isDynamicImage) {
            const dynamicImage = element as ImagePlaceholderElement;

            dynamicImage.set('isColorOptions', !dynamicImage.isColorOptions);
            dynamicImage.setEditedProp('isColorOptions');
            canvas.fire('object:modified', {
                target: dynamicImage
            });

            // If color options was set for this element, make sure no other element is marked for color options, only
            //  one element can be marked for color options
            if (dynamicImage.isColorOptions) {
                (canvas.getObjects() as CanvasElement[])
                    .filter((el) => el !== dynamicImage)
                    .forEach((el) => {
                        if (el.type === 'image-placeholder' && el.dynamicImagesPath && el.isColorOptions) {
                            el.set('isColorOptions', false);
                            el.setEditedProp('isColorOptions');
                            canvas.fire('object:modified', {
                                target: el
                            });
                        }
                    });
            }

            canvas.requestRenderAll();
            return dynamicImage.isColorOptions;
        }
    }

    return false;
};

export const clearBorder = (element: CanvasElement) => {
    if (element.type === 'path-itext') {
        element.set('pathWidth', 0);
    } else if (element.type === 'bounded-text') {
        element.setStrokeWidth(0);
    } else if (element.type === 'text-transform') {
        element.setBoundingStrokeWidth(0);
    } else {
        (element as BaseCanvasElement).set('strokeWidth', 0);
    }
};

export const canGroupElements = (elements: BaseCanvasElement[], groups?: ElementsGroup[], isLayoutOptimized = true) => {
    if (!elements.length) {
        return false;
    }

    const areElementsWithoutGroup = (elements: BaseCanvasElement[]) => elements.every((element) => !element.groupId);
    if (areElementsWithoutGroup(elements)) return true;

    const areElementsInSameGroup = (elements: BaseCanvasElement[]) =>
        elements.every((element) => element.groupId === elements[0].groupId);

    const group = groups?.find((group) => group.id === elements[0].groupId);
    // Allow grouping if all selected elements do not belong to a group
    // Update: Allow grouping if all selected elements belong to the same group too
    return areElementsInSameGroup(elements) && isLayoutOptimized && !group?.parentId;
};

export const canUnGroupElements = (elements: BaseCanvasElement[]) => {
    if (!elements.length) {
        return false;
    }

    // If any of the selected elements belong to a group, allow ungrouping
    return elements.some((element) => element.groupId);
};

export const removeEmptyGroups = (canvas: CanvasEx, groupIds: string[], elements = canvas.getObjects()) => {
    const ids = new Set(groupIds);
    const removedGroups: ElementsGroup[] = [];
    for (const groupId of ids) {
        const childGroups = canvas.getElementsGroups().filter((g) => g.parentId === groupId);
        if (!elements.some((object) => object.groupId === groupId) && !childGroups.length) {
            const group = canvas.removeGroup(groupId);
            removedGroups.push(group!);
        }
    }
    return removedGroups;
};

export const removeElementsByUuid = (canvas: CanvasEx, elements: BaseCanvasElement[]) => {
    const canvasElements = canvas.getObjects();
    const elementsToRemove = canvasElements.filter((o) => elements.some((e) => e.uuid == o.uuid));
    const groupIds: string[] = [];
    canvas.discardActiveObject();
    for (const element of elementsToRemove) {
        if (element.groupId) groupIds.push(element.groupId);
        canvas.remove(element);
    }
    removeEmptyGroups(canvas, groupIds, canvasElements);
};

export const pxToPt = (px: number, DPI = 300) => {
    return (px * 72) / DPI;
};

export const ptToPx = (pt: number, DPI = 300) => {
    return (pt * DPI) / 72;
};

export const getTreeItems = (data: {
    pageEpsElements: ElementsPageEps[];
    canvasElements: CanvasElement[];
    groupElements: ElementsGroup[];
}): ListElements[] => {
    try {
        const { pageEpsElements, canvasElements, groupElements } = data;

        if (!pageEpsElements || !canvasElements || !groupElements) {
            return [];
        }

        if (pageEpsElements.length <= 0 && canvasElements.length <= 0 && groupElements.length <= 0) {
            return [];
        }

        const items: ListElements[] = pageEpsElements.map((pageEps) => {
            const pageEpsItem: ListElements = {
                type: ListElementType.PageEps,
                element: pageEps,
                parent: undefined,
                collapsed: false, // Page eps are expanded by default
                children: [],
                zIndex: pageEps.zIndex
            };

            pageEpsItem.children = getPageEpsChildren(
                pageEps,
                {
                    groupElements,
                    canvasElements
                },
                pageEpsItem
            );

            return pageEpsItem;
        });

        const filteredRootCanvasElements = canvasElements.filter((item) => isRootCanvasElementValid(item));
        const rootCanvasElements = filteredRootCanvasElements.map((canvasElement) => ({
            type: ListElementType.CanvasElement,
            element: canvasElement,
            collapsed: true,
            parent: undefined,
            zIndex: canvasElement.zIndex
        }));

        if (rootCanvasElements && rootCanvasElements.length > 0) {
            items.push(...rootCanvasElements);
        }

        const filteredRootGroupElements = groupElements.filter(
            (groupElement) => !groupElement.parentId && !groupElement.pageEpsId
        );

        const rootGroups = filteredRootGroupElements.map((group) => {
            const groupItem: ListElements = {
                type: ListElementType.Group,
                element: group,
                collapsed: true,
                parent: undefined,
                children: [],
                zIndex: group.zIndex
            };

            groupItem.children = getGroupChildren(
                group,
                {
                    groupElements,
                    canvasElements
                },
                groupItem
            );

            return groupItem;
        });

        if (rootGroups && rootGroups.length > 0) {
            items.push(...rootGroups);
        }

        sortItemsByZIndex(items);

        return items;
    } catch (error) {
        console.error('An error occurred trying to get the tree items', error);
        return [];
    }
};

const sortItemsByZIndex = (items: ListElements[]) => {
    return items.sort((a, b) => b.zIndex - a.zIndex);
};

const filterCanvasElementsByGroupId = (data: {
    group: ElementsGroup;
    canvasElements: CanvasElement[];
    groups?: ElementsGroup[];
}) => {
    const { group, canvasElements, groups } = data;
    if (!group || !canvasElements?.length) return [];

    const targetGroup = groups && groups.length > 0 ? groups.find((g) => g.uuid === group.uuid) ?? group : group;

    const filteredCanvasElements = canvasElements.filter(
        (element) => element.groupId && element.groupId === targetGroup.id
    );

    return filteredCanvasElements;
};

const getGroupChildren = (
    parentGroup: ElementsGroup,
    data: {
        canvasElements: CanvasElement[];
        groupElements: ElementsGroup[];
    },
    parentItem: ListElements
): ListElements[] => {
    const childCanvasElements: ListElements[] = filterCanvasElementsByGroupId({
        group: parentGroup,
        canvasElements: data?.canvasElements,
        groups: data?.groupElements
    }).map((element) => ({
        type: ListElementType.CanvasElement,
        element,
        collapsed: true,
        parent: parentItem,
        children: undefined,
        zIndex: element.zIndex
    }));

    const childGroups: ListElements[] = filterGroupsByParentId({
        parentGroup,
        groups: data?.groupElements
    }).map((group) => {
        const groupItem: ListElements = {
            type: ListElementType.Group,
            element: group,
            collapsed: true,
            parent: parentItem,
            children: [],
            zIndex: group.zIndex
        };

        groupItem.children = getGroupChildren(group, data, groupItem);

        return groupItem;
    });

    return sortItemsByZIndex([...childCanvasElements, ...childGroups]);
};

const filterGroupsByParentId = (data: { parentGroup: ElementsGroup; groups: ElementsGroup[] }) => {
    const { parentGroup, groups } = data;
    if (!parentGroup || !groups?.length) return [];

    const targetGroup = groups.find((g) => g.uuid === parentGroup.uuid);

    if (!targetGroup) {
        return [];
    }

    return groups.filter((group) => group.parentId && group.parentId.toString() === targetGroup.id.toString());
};

const getPageEpsChildren = (
    pageEps: ElementsPageEps,
    data: {
        canvasElements: CanvasElement[];
        groupElements: ElementsGroup[];
    },
    parentItem: ListElements
) => {
    const pageEpsId = pageEps?.id.toString();
    const canvasElements = filterCanvasElementsByPageEpsId(pageEpsId, data?.canvasElements).map((element) => ({
        type: ListElementType.CanvasElement,
        element,
        collapsed: true,
        parent: parentItem,
        children: undefined,
        zIndex: element.zIndex
    }));

    const parentGroups = filterParentGroupsByPageEpsId(pageEpsId, data?.groupElements).map((group) => {
        const groupItem: ListElements = {
            type: ListElementType.Group,
            element: group,
            collapsed: true,
            parent: parentItem,
            children: [],
            zIndex: group.zIndex
        };

        groupItem.children = getGroupChildren(group, data, groupItem);

        return groupItem;
    });

    const sortedListElements = sortItemsByZIndex([...canvasElements, ...parentGroups]);
    return sortedListElements;
};

const filterParentGroupsByPageEpsId = (pageEpsId: string, groupElements: ElementsGroup[]) => {
    return groupElements.filter((group) => {
        // If the group has a parentId, but the parent group does not exist, set parentId to null
        if (group.pageEpsId) {
            const parentGroup = groupElements.find((g) => g.id === group.parentId);
            if (!parentGroup) {
                group.parentId = null;
            }
        }

        return group.pageEpsId && group.pageEpsId.toString() === pageEpsId.toString() && !group.parentId;
    });
};

const filterCanvasElementsByPageEpsId = (pageEpsId: string, canvasElements: CanvasElement[]) => {
    return canvasElements.filter(
        (element) => element.pageEpsId && element.pageEpsId.toString() === pageEpsId.toString() && !element.groupId
    );
};

// Update the zIndex of eps to reconstruct the order of the root canvas elements in the tree
export const updateEpsZIndex = (items: ListElements[]) => {
    const flattenListElements = flattenItems(items);
    const totalElements = flattenListElements.length;

    const affectedItems = updateZIndices(flattenListElements, totalElements);
    if (affectedItems.length === 0) return;
    sortItemsByZIndex(items);
};

export const updateElementOrder = debounce((items: ListElements[], canvas: CanvasEx, updateZIndex = false) => {
    try {
        if (items.length === 0) return;

        const flattenListElements = flattenItems(items);
        const totalElements = flattenListElements.length;

        if (updateZIndex) {
            updateZIndices(flattenListElements, totalElements);
        }

        moveCanvasElements(flattenListElements, canvas);
        canvas.requestRenderAll();
    } catch (error) {
        console.error('An error occurred trying to update the order of the tree items', error);
    }
}, 1000);

const updateZIndices = (elements: ListElements[], totalElements: number, type?: ListElementType) => {
    const affectedItems: ListElements[] = [];
    elements.forEach((item, index) => {
        // If the item is not of the specified type, skip it
        // If no type is specified, update all items
        if (!(item.type === type || type === undefined)) return;
        const zIndex = totalElements - index;

        if (item.zIndex === zIndex && item.element.zIndex === zIndex) return;

        affectedItems.push(item);
        item.zIndex = zIndex;
        item.element.zIndex = zIndex;
    });
    return affectedItems;
};

const moveCanvasElements = (elements: ListElements[], canvas: CanvasEx) => {
    const canvasElements = elements.filter((item) => item.type === ListElementType.CanvasElement);
    canvasElements.forEach((item, index) => {
        const canvasElementIndex = canvasElements.length - 1 - index;
        canvas.moveTo(item.element as CanvasElement, canvasElementIndex);
    });
};

export const flattenItems = (items: ListElements[]): ListElements[] => {
    const result: ListElements[] = [];
    for (const item of items) {
        result.push(item);
        if (item.children && item.children.length > 0) {
            result.push(...flattenItems(item.children));
        }
    }
    return result;
};

/**
 * Compares two elements by uuid or id. Returns true if they are considered equal.
 * - If both have uuid, compares uuid.
 * - If neither has uuid, compares id.
 * - Otherwise, returns false.
 */
export const areElementsEqualByUuidOrId = (a: ListElements, b: ListElements): boolean => {
    if (a.element?.uuid && b.element?.uuid) {
        return a.element.uuid === b.element.uuid;
    }
    if (!a.element?.uuid && !b.element?.uuid) {
        return a.element?.id === b.element?.id;
    }
    return false;
};
